/**
 * Pure day-bucketing helper for any timestamped list (originally the clipboard
 * screen's history, C9,
 * `docs/superpowers/specs/2026-08-05-clipboard-card-language-design.md`).
 * Mirrors the shape of `getDateLabel`/`buildTimelineBuckets` in
 * `SidePanelNotificationList/helpers.ts` — same local-calendar-day boundary
 * logic — but generalized to take `now` as an explicit parameter instead of
 * reading `Date.now()` internally, so callers (and their tests) get fully
 * deterministic output from fixed timestamps rather than depending on the
 * wall clock.
 *
 * Formatting is deliberately NOT this helper's job: it returns a `key`
 * ("today" | "yesterday" | "older") plus each group's local-calendar-day
 * start as a raw epoch-ms `date`. The caller resolves `key` through its own
 * (possibly-missing) i18n labels and formats `date` via
 * `toLocaleDateString` for "older" groups — see `ClipboardScreen`'s
 * `dayGroupLabel` (that method, not a manually-constructed
 * `Intl.DateTimeFormat`, so a malformed `NaN` timestamp degrades to the
 * string "Invalid Date" instead of throwing). Keeping locale/label concerns
 * out of this module is what keeps it trivially unit-testable with fixed
 * timestamps.
 *
 * Callers should recompute this at a calendar-day boundary — the group
 * membership is only as fresh as the `now` passed in. `ClipboardScreen`
 * re-derives via a midnight `setTimeout` tick, mirroring
 * `useNotificationListView`'s identical pattern for the notification list's
 * own Today/Yesterday buckets.
 *
 * SECURITY: only the timestamp `getTimestamp` returns is read. Item content is
 * never inspected, compared, or logged by this module — which is what lets an
 * SMS thread share this helper with the clipboard history.
 */
export type DayGroupKey = "today" | "yesterday" | "older";

export interface DayGroup<T> {
  key: DayGroupKey;
  /** Local calendar-day start (midnight) for this group's items, epoch ms. */
  date: number;
  /** Newest-first within the group. */
  items: T[];
}

/** Local calendar-day start (midnight) for a timestamp, epoch ms. */
function startOfDay(timestamp: number): number {
  const d = new Date(timestamp);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/**
 * Partition `items` into day groups: Today, Yesterday, then each older
 * calendar day as its own group (all tagged `"older"`, distinguished by
 * `date`). Ordered newest-group-first; within a group, newest-item-first.
 * Input order is not assumed — the items are sorted internally.
 *
 * Generic over the item type, with the timestamp supplied by `getTimestamp`,
 * so a caller reads whichever field means DISPLAY time for it. That matters
 * for SMS: `SmsThreadMessage` carries `sortTimestamp` as a separate PAGING
 * axis, and grouping on it would reorder a delayed-delivery message whose
 * service-center time is hours older than its arrival.
 *
 * An item with a timestamp exactly equal to a day's start (00:00:00.000)
 * belongs to THAT day, not the previous one (`startOfDay` returns the same
 * value for both the boundary timestamp and any other timestamp on that
 * day, and group membership is a `===` check against it).
 *
 * Returns `[]` for an empty `items` list.
 */
export function groupByDay<T>(
  items: readonly T[],
  now: number,
  getTimestamp: (item: T) => number
): DayGroup<T>[] {
  const todayStart = startOfDay(now);
  // Calendar-component subtraction — NOT `todayStart - 86_400_000`. A day is
  // not always 24 real hours: a DST spring-forward day is 23h, a fall-back
  // day is 25h. On the day immediately after either kind of transition, a
  // fixed-ms subtraction lands on an hour that is not any calendar day's
  // local midnight, so the exact `===` group-key match below never fires
  // and yesterday's items silently fall through to "older" (reproduced in
  // America/Los_Angeles for 2025-03-10 and 2025-11-03 — see the DST
  // fixtures in groupByDay.test.ts, which pin this).
  //
  // `getDateLabel` (SidePanelNotificationList/helpers.ts) hits the same
  // class of bug and mitigates it with a range test (`timestamp >=
  // threshold`) instead of exact equality — a real improvement over a naive
  // equality check, but still not exact: its own `todayStart -
  // 86_400_000` yesterday threshold can land an hour *late* on a fall-back
  // day, so an item in the first hour of "yesterday" would still miss the
  // "yesterday" bucket there. This function goes one step further and
  // computes `yesterdayStart` the SAME way `todayStart` already is — via
  // `Date`-component construction (`year, month, date - 1`), not ms
  // arithmetic — so both sides of every `===` comparison below are built
  // identically by construction and stay exact through a DST transition in
  // either direction, rather than trading one edge case for a smaller one.
  const nowDate = new Date(now);
  const yesterdayStart = new Date(
    nowDate.getFullYear(),
    nowDate.getMonth(),
    nowDate.getDate() - 1
  ).getTime();

  const sorted = [...items].sort((a, b) => getTimestamp(b) - getTimestamp(a));

  const groups: DayGroup<T>[] = [];
  let currentDayStart: number | undefined;

  for (const item of sorted) {
    const dayStart = startOfDay(getTimestamp(item));
    if (dayStart !== currentDayStart) {
      currentDayStart = dayStart;
      const key: DayGroupKey =
        dayStart === todayStart ? "today" : dayStart === yesterdayStart ? "yesterday" : "older";
      groups.push({ key, date: dayStart, items: [item] });
    } else {
      groups[groups.length - 1].items.push(item);
    }
  }

  return groups;
}
